import {ellipsis} from 'polished';
import React, {FC, useCallback, useMemo, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ApplicationModel} from '../../../../../models/api/applicationModel';
import {topicDataEventTracker} from '../../../../../services/tracking/topic';
import {makeGetConversationById} from '../../../../../store/data/conversations/conversationsSelectors';
import {removeTopicFromConversationsAction} from '../../../../../store/data/conversations/topicConversationsActions';
import {getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {makeGetApplicationForTopic, makeGetTopic} from '../../../../../store/data/topics/topicsSelectors';
import {ConversationSummaryContainer} from '../../conversation/conversationSummary/conversationSummaryContainer';
import {ConversationSummarySectionsEnum} from '../../conversation/conversationSummary/conversationSummarySections';
import {TopicDropdownContainer} from '../../conversation/header/topic/topicDropdownContainer';
import {HoverTopicReferencePreviewerUnfurl} from './hoverTopicReferencePreviewerUnfurl';

/*
 * Props.
 */

interface ActivityReferencePreviewerPillProps {
  panelId: ViewerPanelId;
  conversationId: number;
  topicId: number;
}

/*
 * Style.
 */

interface StyledPillDivProps {
  $hasHover: boolean;
}

const StyledPillDiv = styled.div<StyledPillDivProps>`
  ${ellipsis('200px')};
  display: inline-flex;
  flex-flow: row;
  gap: 4px;
  margin-top: 1px;

  max-width: 200px;
  padding: 1px 3px;

  background: ${(p) => p.theme.greys.shade10};
  border: 1px solid ${(p) => p.theme.greys.shade70};
  border-radius: 2px;

  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};

  ${(p) =>
    p.$hasHover &&
    css`
      background: ${p.theme.greys.shade30};
    `}

  &:hover {
    background: ${(p) => p.theme.greys.shade30};
  }
`;

const StyledNameWrapperDiv = styled.div`
  display: inline;
  color: ${(p) => p.theme.palette.blue.shade40};

  ${ellipsis()}
`;

const StyledApplicationWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
`;

const StyledApplicationImg = styled.img`
  width: 12px;
  height: 12px;
`;

/*
 * Component.
 */

export const ActivityReferencePreviewerPill: FC<ActivityReferencePreviewerPillProps> = (props) => {
  const {panelId, conversationId, topicId} = props;
  const getTopic = useMemo(() => makeGetTopic(() => topicId), [topicId]);
  const topic = useAppSelector(getTopic);
  const getApplication = useMemo(() => makeGetApplicationForTopic(topic), [topic]);
  const application = useAppSelector(getApplication);
  const teammateId = useAppSelector(getUserId);
  const getConversation = useMemo(() => makeGetConversationById(conversationId), [conversationId]);
  const conversation = useAppSelector(getConversation);
  const [isConversationSummaryOpen, setIsConversationSummaryOpen] = useState(false);
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();
  const anchor = useRef<HTMLDivElement>(null);
  const [isTopicDropdownOpen, setIsTopicDropdownOpen] = useState(false);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);

  const trackAction = useCallback(
    (action: DataEventActionsEnum) => {
      if (!topic) {
        return;
      }

      trackDataEvent(...topicDataEventTracker(action, topic));
    },
    [topic, trackDataEvent],
  );

  if (!topic || !conversation) {
    return null;
  }

  const removeTopic = () => {
    dispatch(removeTopicFromConversationsAction([conversationId], topic.id));
  };

  const maybeRenderConversationSummary = () =>
    isConversationSummaryOpen ? (
      <ConversationSummaryContainer
        conversation={conversation}
        teammateId={teammateId}
        onRequestClose={() => setIsConversationSummaryOpen(false)}
        initiallyExpandedSection={ConversationSummarySectionsEnum.REFERENCE_PREVIEWERS}
        panelId={panelId}
        selectedTopicId={topic.id}
      />
    ) : null;

  const onPillClick = () => {
    setShouldShowPreview(false);
    setIsTopicDropdownOpen(true);
    if (isTopicDropdownOpen) {
      trackAction(DataEventActionsEnum.CLICK);
    }
  };

  const onTopicEnter = () => {
    setShouldShowPreview(true);
  };

  const onTopicLeave = () => {
    setShouldShowPreview(false);
  };

  return (
    <div onMouseEnter={onTopicEnter} onMouseLeave={onTopicLeave}>
      <StyledPillDiv $hasHover={isTopicDropdownOpen} ref={anchor} onClick={onPillClick}>
        {renderIcon(application)}
        <StyledNameWrapperDiv>{topic.name}</StyledNameWrapperDiv>
      </StyledPillDiv>
      <HoverTopicReferencePreviewerUnfurl
        topic={topic}
        anchor={anchor}
        shouldShowPreview={shouldShowPreview}
      />
      {maybeRenderConversationSummary()}
      {isTopicDropdownOpen && (
        <TopicDropdownContainer
          topic={topic}
          removeTopic={conversation.topicIds.includes(topic.id) ? removeTopic : undefined}
          isApplicationTopic
          onToggleSummary={() => setIsConversationSummaryOpen(!isConversationSummaryOpen)}
          onOpenUrl={() => trackAction(DataEventActionsEnum.OPEN_URL)}
          onRequestClose={() => setIsTopicDropdownOpen(false)}
          anchor={anchor}
        />
      )}
    </div>
  );
};

/*
 * Helpers.
 */

function renderIcon(application?: ApplicationModel) {
  if (application && application.iconUrl) {
    return (
      <StyledApplicationWrapperDiv>
        <StyledApplicationImg src={application.iconUrl} />
      </StyledApplicationWrapperDiv>
    );
  }
  return <Icon name="topicWebSmall" size={12} />;
}
